import { supabase } from './supabase';

export interface InventoryValuationRow {
  inventory_item_id: string;
  item_name: string;
  item_code: string | null;
  outlet_id: string;
  stock_location_id: string;
  location_name: string;
  quantity_on_hand: number;
  average_cost: number;
  total_value: number;
}

export interface ValuationSummary {
  totalValue: number;
  totalItems: number;
  totalLocations: number;
}

export const fetchInventoryValuation = async (
  companyId: number,
  outletId?: string | null,
): Promise<{ rows: InventoryValuationRow[]; summary: ValuationSummary }> => {
  let query = supabase
    .from('inventory_stock_balances')
    .select(
      'inventory_item_id, outlet_id, stock_location_id, quantity_on_hand, average_cost, total_value, inventory_item:inventory_items(name, sku), stock_location:inventory_stock_locations(name)',
    )
    .eq('company_id', companyId)
    .gt('quantity_on_hand', 0)
    .order('total_value', { ascending: false });

  if (outletId) query = query.eq('outlet_id', outletId);

  const { data } = await query;

  const rows: InventoryValuationRow[] = (
    (data ?? []) as Array<Record<string, unknown>>
  ).map((r) => ({
    inventory_item_id: String(r.inventory_item_id || ''),
    item_name:
      (r.inventory_item as { name?: string } | null)?.name ?? 'Unknown item',
    item_code:
      (r.inventory_item as { sku?: string | null } | null)?.sku ?? null,
    outlet_id: String(r.outlet_id || ''),
    stock_location_id: String(r.stock_location_id || ''),
    location_name:
      (r.stock_location as { name?: string } | null)?.name ??
      'Unknown location',
    quantity_on_hand: Number(r.quantity_on_hand || 0),
    average_cost: Number(r.average_cost || 0),
    total_value: Number(r.total_value || 0),
  }));

  const totalValue = rows.reduce((s, r) => s + r.total_value, 0);
  const uniqueItems = new Set(rows.map((r) => r.inventory_item_id)).size;
  const uniqueLocations = new Set(rows.map((r) => r.stock_location_id)).size;

  return {
    rows,
    summary: {
      totalValue,
      totalItems: uniqueItems,
      totalLocations: uniqueLocations,
    },
  };
};

export interface TheoVsActualRow {
  inventory_item_id: string;
  item_name: string;
  theoretical_usage: number;
  actual_usage: number;
  variance: number;
  variance_pct: number;
  avg_cost: number;
  variance_value: number;
}

export const fetchTheoreticalVsActual = async (
  companyId: number,
  outletId: string,
  dateFrom: string,
  dateTo: string,
): Promise<TheoVsActualRow[]> => {
  // Actual usage: sum of sale_consumption movements in period
  let actualQuery = supabase
    .from('inventory_stock_movements')
    .select('inventory_item_id, quantity, unit_cost')
    .eq('company_id', companyId)
    .eq('movement_type', 'sale_consumption')
    .gte('movement_date', dateFrom)
    .lte('movement_date', `${dateTo}T23:59:59`);

  if (outletId && outletId !== 'all')
    actualQuery = actualQuery.eq('outlet_id', outletId);

  const { data: actualData } = await actualQuery;

  const actualMap = new Map<
    string,
    { qty: number; totalCost: number; count: number }
  >();
  ((actualData ?? []) as Array<Record<string, unknown>>).forEach((row) => {
    const id = String(row.inventory_item_id || '');
    const qty = Math.abs(Number(row.quantity || 0));
    const cost = Number(row.unit_cost || 0);
    const cur = actualMap.get(id) ?? { qty: 0, totalCost: 0, count: 0 };
    cur.qty += qty;
    cur.totalCost += cost;
    cur.count += 1;
    actualMap.set(id, cur);
  });

  if (actualMap.size === 0) return [];

  // Theoretical usage: recipe qty × number of sold menu items
  // We approximate from sale_consumption movements directly vs recipe-derived amounts
  // Since we don't have an orders-by-menu-item-with-recipe join here, we fetch
  // recipes to compute expected-per-unit, then compare to actual movements
  const itemIds = Array.from(actualMap.keys());

  const { data: recipeItems } = await supabase
    .from('inventory_recipe_items')
    .select(
      'inventory_item_id, quantity, recipe:inventory_recipes(company_id, is_active)',
    )
    .in('inventory_item_id', itemIds);

  const { data: itemData } = await supabase
    .from('inventory_items')
    .select('id, name')
    .eq('company_id', companyId)
    .in('id', itemIds);

  const nameMap = new Map<string, string>(
    ((itemData ?? []) as Array<Record<string, unknown>>).map((i) => [
      String(i.id || ''),
      String(i.name || ''),
    ]),
  );

  // Build avg qty-per-sale from recipes: sum of recipe_item.quantity across active recipes
  const recipeQtyMap = new Map<string, number>();
  ((recipeItems ?? []) as Array<Record<string, unknown>>).forEach((ri) => {
    const recipe = ri.recipe as {
      company_id?: number;
      is_active?: boolean;
    } | null;
    if (recipe?.is_active && recipe.company_id === companyId) {
      const id = String(ri.inventory_item_id || '');
      recipeQtyMap.set(
        id,
        (recipeQtyMap.get(id) ?? 0) + Number(ri.quantity || 0),
      );
    }
  });

  const { data: avgCostData } = await supabase
    .from('inventory_stock_balances')
    .select('inventory_item_id, average_cost, quantity_on_hand')
    .eq('company_id', companyId)
    .gt('quantity_on_hand', 0);

  const avgCostMap = new Map<string, number>();
  const totals = new Map<string, { c: number; q: number }>();
  ((avgCostData ?? []) as Array<Record<string, unknown>>).forEach((r) => {
    const id = String(r.inventory_item_id || '');
    const q = Number(r.quantity_on_hand || 0);
    const c = Number(r.average_cost || 0);
    const cur = totals.get(id) ?? { c: 0, q: 0 };
    cur.c += q * c;
    cur.q += q;
    totals.set(id, cur);
  });
  Array.from(totals).forEach(([id, { c, q }]) => {
    avgCostMap.set(id, q > 0 ? c / q : 0);
  });

  const rows: TheoVsActualRow[] = [];
  Array.from(actualMap).forEach(([itemId, actual]) => {
    const theoretical = recipeQtyMap.get(itemId) ?? 0;
    const variance = actual.qty - theoretical;
    const avgCost = avgCostMap.get(itemId) ?? 0;
    rows.push({
      inventory_item_id: itemId,
      item_name: nameMap.get(itemId) ?? 'Unknown item',
      theoretical_usage: theoretical,
      actual_usage: actual.qty,
      variance,
      variance_pct: theoretical > 0 ? (variance / theoretical) * 100 : 0,
      avg_cost: avgCost,
      variance_value: Math.abs(variance) * avgCost,
    });
  });

  return rows.sort((a, b) => b.variance_value - a.variance_value);
};

export interface MenuItemProfitabilityRow {
  menu_item_id: string;
  menu_item_name: string;
  selling_price: number;
  recipe_cost: number;
  gross_profit: number;
  food_cost_pct: number;
  has_recipe: boolean;
}

export const fetchMenuItemProfitability = async (
  companyId: number,
  outletId?: string | null,
): Promise<MenuItemProfitabilityRow[]> => {
  // Fetch all active recipes with their items
  let recipeQuery = supabase
    .from('inventory_recipes')
    .select(
      'id, menu_item_id, yield_quantity, is_active, items:inventory_recipe_items(inventory_item_id, quantity)',
    )
    .eq('company_id', companyId)
    .eq('is_active', true);

  if (outletId) recipeQuery = recipeQuery.eq('outlet_id', outletId);

  const { data: recipes } = await recipeQuery;

  // Fetch menu items with their base price
  let menuQuery = supabase
    .from('menu_items')
    .select('id, name, base_price')
    .eq('company_id', companyId)
    .eq('status', 'active');

  if (outletId) menuQuery = menuQuery.eq('outlet_id', outletId);

  const { data: menuItems } = await menuQuery;

  // Cost map for ingredient pricing
  const costMap = new Map<string, number>();
  const { data: balances } = await supabase
    .from('inventory_stock_balances')
    .select('inventory_item_id, average_cost, quantity_on_hand')
    .eq('company_id', companyId)
    .gt('quantity_on_hand', 0);

  const totals = new Map<string, { c: number; q: number }>();
  ((balances ?? []) as Array<Record<string, unknown>>).forEach((r) => {
    const id = String(r.inventory_item_id || '');
    const q = Number(r.quantity_on_hand || 0);
    const c = Number(r.average_cost || 0);
    const cur = totals.get(id) ?? { c: 0, q: 0 };
    cur.c += q * c;
    cur.q += q;
    totals.set(id, cur);
  });
  Array.from(totals).forEach(([id, { c, q }]) => {
    costMap.set(id, q > 0 ? c / q : 0);
  });

  // Build recipe cost per menu item
  const recipeCostByMenuItem = new Map<string, number>();
  ((recipes ?? []) as Array<Record<string, unknown>>).forEach((recipe) => {
    const menuItemId = String(recipe.menu_item_id || '');
    const yieldQty = Number(recipe.yield_quantity || 1);
    const items = Array.isArray(recipe.items)
      ? (recipe.items as Array<Record<string, unknown>>)
      : [];
    const totalIngredientCost = items.reduce((sum, item) => {
      return (
        sum +
        Number(item.quantity || 0) *
          (costMap.get(String(item.inventory_item_id || '')) ?? 0)
      );
    }, 0);
    recipeCostByMenuItem.set(
      menuItemId,
      totalIngredientCost / Math.max(yieldQty, 1),
    );
  });

  const rows: MenuItemProfitabilityRow[] = (
    (menuItems ?? []) as Array<Record<string, unknown>>
  ).map((m) => {
    const menuItemId = String(m.id || '');
    const sellingPrice = Number(m.base_price || 0);
    const hasRecipe = recipeCostByMenuItem.has(menuItemId);
    const recipeCost = recipeCostByMenuItem.get(menuItemId) ?? 0;
    const grossProfit = sellingPrice - recipeCost;
    return {
      menu_item_id: menuItemId,
      menu_item_name: String(m.name || ''),
      selling_price: sellingPrice,
      recipe_cost: recipeCost,
      gross_profit: grossProfit,
      food_cost_pct: sellingPrice > 0 ? (recipeCost / sellingPrice) * 100 : 0,
      has_recipe: hasRecipe,
    };
  });

  return rows.sort((a, b) => b.gross_profit - a.gross_profit);
};
